import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSupabase } from '../lib/supabase.js';
import './cost-lines.css';

// T-051 (owner 2026-09-24): every investor sees their household's costs line
// by line, and can edit the lines THEY entered — a change to a booked line
// goes to Kareem for confirmation; a change to a still-pending line applies
// in place. An admin acting as the investor edits directly (and can remove).
//
// Layout (2026-10-23 redesign): one quiet row per cost — short title, who,
// amount — and a panel that opens on tap with the full detail, the edit
// history and the actions. Chips mark exceptions only; a booked line wears
// none. Month headers carry the booked subtotal, matching the card header.
//
// T-063 (owner 2026-10-23): the card also lists what is COMING UP on the
// household's cost schedules (one row per live schedule, its next bill date)
// and, once the day arrives and the nightly job has turned the occurrence
// into an "expected" line, the payer confirms it right here: Confirm charge
// books it, Didn't happen skips it, an edited amount goes to Kareem as a
// request. Rows that need the viewer's answer start open so the buttons are
// visible without a tap. Same RPCs as the red Home card (CostSuggestionsCard).
//
// Mounts on Balances (investor + act-as). The admin's cross-household ledger
// with the same actions lives on Catalog admin → Costs.
//
// Editable for an investor = source 'app' (their one-time entries) or
// 'schedule' occurrences they confirmed, paid_by 'investor', status
// confirmed or pending. Kareem-entered lines are read-only here.

const fmt$ = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthLabel = (iso) => { try { return new Date(String(iso).slice(0, 10) + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }); } catch { return iso; } };
const dayLabel = (iso) => { try { return new Date(String(iso).slice(0, 10) + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }); } catch { return iso; } };

export const STATUS_LABEL = {
  confirmed: 'In the books', pending: 'Awaiting Kareem', suggested: 'Expected — confirm it', skipped: 'Did not happen',
  rejected: 'Rejected', removed: 'Removed', reference: 'Assumed history',
};

export function isInvestorEditable(it) {
  return (it.source === 'app' || it.source === 'schedule') && it.paid_by === 'investor'
    && (it.status === 'confirmed' || it.status === 'pending');
}

// The note doubles as the invoice: "Vendor · invoice date · card · detail".
// The first segment is the title; the rest is detail for the panel. Stray
// trailing separators left behind by edits are trimmed.
export function splitNote(note) {
  const clean = String(note || '').replace(/[\s·|,-]+$/g, '').trim();
  const i = clean.indexOf(' · ');
  if (i < 0) return { title: clean, detail: '' };
  return { title: clean.slice(0, i).trim(), detail: clean.slice(i + 3).trim() };
}

// review_note is a " | "-joined audit trail written by the SQL functions:
//   "edited by Kareem 2026-10-20: was $40, note was "…" — reason"
//   "removed by Kareem 2026-09-24 (was confirmed $120) — duplicate"
export function parseHistory(reviewNote) {
  return String(reviewNote || '').split(' | ').map((s) => s.trim()).filter(Boolean).map((raw) => {
    const m = raw.match(/^(edited|removed|rejected|declined|confirmed|skipped)\s+by\s+(.+?)\s+(\d{4}-\d{2}-\d{2})\s*[:(]?\s*(.*)$/i);
    if (!m) return { raw, verb: null, who: null, date: null, rest: raw, reason: null };
    let rest = m[4];
    let reason = null;
    const dash = rest.indexOf(' — ');
    if (dash >= 0) { reason = rest.slice(dash + 3).trim(); rest = rest.slice(0, dash).trim(); }
    rest = rest.replace(/\)\s*$/, '');
    return { raw, verb: m[1].toLowerCase(), who: m[2], date: m[3], rest, reason };
  });
}

const CHIP = {
  pending: ['#fffbeb', '#92400e', '#fde68a'], suggested: ['#fef2f2', '#b91c1c', '#fecaca'],
  skipped: ['#f1f5f9', '#475569', '#e2e8f0'], rejected: ['#fef2f2', '#b91c1c', '#fecaca'],
  removed: ['#f1f5f9', '#475569', '#e2e8f0'], reference: ['#f1f5f9', '#475569', '#e2e8f0'],
  confirmed: ['#ecfdf5', '#047857', '#a7f3d0'],
};
const chipStyle = ([bg, fg, bd]) => ({ background: bg, color: fg, borderColor: bd });

// Exported for Catalog admin; the card itself shows no chip on a booked line.
export function StatusChip({ it }) {
  const s = it.status || 'confirmed';
  return <span className="cl-chip" style={chipStyle(CHIP[s] || CHIP.confirmed)}>{STATUS_LABEL[s] || s}</span>;
}

const S = {
  card: { background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e8f0)', borderRadius: 16, padding: '14px 16px', margin: '0 0 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  eye: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--accent-2,#3730a3)' },
  title: { fontSize: 15, fontWeight: 800, margin: '2px 0 2px' },
  sub: { fontSize: 12, color: 'var(--text-2,#475569)', margin: '0 0 8px', lineHeight: 1.45 },
  btn: { fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border-2,#d4d8e6)', background: 'var(--surface,#fff)', color: 'var(--text-2,#334155)', cursor: 'pointer' },
  empty: { fontSize: 13, color: 'var(--text-3,#94a3b8)', padding: '8px 0' },
  err: { fontSize: 12, color: '#b91c1c', marginTop: 6 },
};

// Shared edit dialog: investor → request; admin → direct edit.
export function CostEditModal({ item, mode, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(item.amount));
  const [note, setNote] = useState(item.note || '');
  const [month, setMonth] = useState(String(item.relates_to_month || item.month || '').slice(0, 7));
  const [date, setDate] = useState(String(item.occurred_on || '').slice(0, 10));   // T-064
  const today = new Date().toISOString().slice(0, 10);
  const onDate = (v) => { setDate(v); if (/^\d{4}-\d{2}/.test(v)) setMonth(v.slice(0, 7)); };
  const [paidBy, setPaidBy] = useState(item.paid_by || 'investor');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const admin = mode === 'admin';
  const booked = item.status === 'confirmed';

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setError('Enter a positive amount.'); return; }
    if (!note.trim()) { setError('A note is required.'); return; }
    const sb = getSupabase();
    if (!sb) return;
    setSaving(true); setError(null);
    try {
      if (admin) {
        const { error: err } = await sb.rpc('v2_admin_edit_cost_item', {
          p_item_id: item.id, p_amount: amt, p_note: note.trim(), p_month: month ? month + '-01' : null,
          p_paid_by: paidBy, p_reason: reason.trim() || null, p_occurred_on: date || null,
        });
        if (err) throw err;
        onSaved && onSaved({ mode: 'admin' });
      } else {
        const { data, error: err } = await sb.rpc('v2_request_cost_item_edit', {
          p_item_id: item.id, p_amount: amt, p_note: note.trim(), p_reason: reason.trim() || null, p_occurred_on: date || null,
        });
        if (err) throw err;
        onSaved && onSaved({ mode: (data && data.mode) || (booked ? 'pending_edit' : 'edited_pending') });
      }
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  const LBL = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--text-3,#94a3b8)' };
  const INP = { font: 'inherit', padding: '8px 10px', border: '1px solid var(--border-2,#d4d8e6)', borderRadius: 8, background: 'var(--surface,#fff)', color: 'var(--text-1,#0f172a)' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div className="cost-edit-modal" style={{ background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e8f0)', borderRadius: 14, padding: '18px 20px', width: '100%', maxWidth: 440, boxShadow: '0 18px 50px rgba(2,6,23,0.35)' }} onClick={(e) => e.stopPropagation()}>
        <div style={S.eye}>{admin ? 'Edit cost line' : booked ? 'Request a change' : 'Edit your entry'}</div>
        <h3 style={{ margin: '2px 0 4px', fontSize: 16, fontWeight: 800 }}>{monthLabel(item.relates_to_month || item.month)} · {fmt$(item.amount)}</h3>
        <p style={S.sub}>
          {admin
            ? 'Books immediately. The previous values stay on the line as an audit note.'
            : booked
              ? 'This line is already in the books. Your change goes to Kareem — the line keeps its current figure until he confirms it.'
              : 'Kareem has not reviewed this yet, so your change applies right away and stays in his queue.'}
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          <label style={LBL}>Amount $<input style={INP} type="number" min="0.01" step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="cost-edit-amount" /></label>
          <label style={LBL}>Note<input style={INP} type="text" value={note} onChange={(e) => setNote(e.target.value)} className="cost-edit-note" /></label>
          <label style={LBL}>Date it happened<input style={INP} type="date" max={today} value={date} onChange={(e) => onDate(e.target.value)} className="cost-edit-date" /></label>
          {admin && (
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...LBL, flex: 1 }}>Month<input style={INP} type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></label>
              <label style={{ ...LBL, flex: 1 }}>Paid by
                <select style={INP} value={paidBy} onChange={(e) => setPaidBy(e.target.value)}>
                  <option value="investor">Investor</option>
                  <option value="kareem">Kareem</option>
                </select>
              </label>
            </div>
          )}
          <label style={LBL}>{admin ? 'Reason (kept on the line)' : 'Why the change? (optional, Kareem sees it)'}<input style={INP} type="text" value={reason} onChange={(e) => setReason(e.target.value)} className="cost-edit-reason" placeholder={admin ? 'e.g. duplicate of the Sep 3 line' : 'e.g. receipt says 45, not 40'} /></label>
        </div>
        {error && <div style={S.err}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" style={S.btn} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary-sm cost-edit-save" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : admin ? 'Save changes' : booked ? 'Send to Kareem' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const HIDDEN_STATUSES = ['removed', 'rejected', 'skipped', 'reference'];
const ordinal = (n) => { const v = Number(n); const s = ['th', 'st', 'nd', 'rd'][(v % 100 > 10 && v % 100 < 14) ? 0 : (v % 10 < 4 ? v % 10 : 0)]; return `${v}${s}`; };
const SCHED_LABEL = { active: null, paused: 'Paused', pending_approval: "Awaiting Kareem's approval" };
const VERB = { edited: 'edited', removed: 'removed the line', rejected: 'declined the change', declined: 'declined the change', confirmed: 'confirmed', skipped: 'marked it as not happened' };
const trunc = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s);

export default function CostLinesCard({ member, members = [], actingAsAdmin = false }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [flash, setFlash] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [memberFilter, setMemberFilter] = useState(null);
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  const [answerOnly, setAnswerOnly] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [inline, setInline] = useState(null);   // { id, value } — editing an expected charge's amount
  const [busy, setBusy] = useState(false);

  const family = useMemo(() => {
    const ids = new Set([member?.id, ...(members || []).map((m) => m.id)].filter(Boolean));
    return ids;
  }, [member?.id, members]);
  const nameOf = useMemo(() => Object.fromEntries((members || []).map((m) => [m.id, m.name])), [members]);

  const load = useCallback(async () => {
    const sb = getSupabase();
    if (!sb || !member) { setItems([]); return; }
    const { data, error: err } = await sb.from('investment_cost_items')
      .select('*')
      .order('month', { ascending: false })
      .order('created_at', { ascending: false });
    if (err) { setError(err.message || String(err)); setItems([]); return; }
    // RLS already scopes to the household; keep the client filter so an
    // admin acting as one household sees only that household.
    const mine = (data || []).filter((it) => family.has(it.member_id));
    setItems(mine);
    // Expected charges the viewer must answer start open: the buttons show
    // without a tap.
    const ask = mine.filter((it) => it.status === 'suggested' && (actingAsAdmin || it.paid_by === 'investor')).map((it) => it.id);
    if (ask.length) setOpen((prev) => new Set([...prev, ...ask]));
    // What is coming up: the household's live schedules, soonest first.
    const { data: sched } = await sb.from('investment_cost_schedules')
      .select('*')
      .in('status', ['active', 'paused', 'pending_approval'])
      .order('next_due', { ascending: true });
    setSchedules((sched || []).filter((sc) => family.has(sc.member_id)));
  }, [member?.id, family, actingAsAdmin]);

  useEffect(() => { load(); }, [load]);

  const isAwaiting = (it) => it.status === 'pending' || !!it.pending_edit;
  const awaitingCount = (items || []).filter(isAwaiting).length;
  // An expected charge is answered by whoever pays it: the investor for
  // investor-paid, Kareem (here: acting as the investor) for Kareem-paid.
  const needsAnswer = (it) => it.status === 'suggested' && (actingAsAdmin || it.paid_by === 'investor');
  const answerCount = (items || []).filter(needsAnswer).length;

  const visible = useMemo(() => (items || []).filter((it) =>
    (showAll || !HIDDEN_STATUSES.includes(it.status))
    && (!memberFilter || it.member_id === memberFilter)
    && (!awaitingOnly || isAwaiting(it))
    && (!answerOnly || needsAnswer(it))), [items, showAll, memberFilter, awaitingOnly, answerOnly, actingAsAdmin]);
  const upcoming = useMemo(() => schedules.filter((sc) => (!memberFilter || sc.member_id === memberFilter) && !awaitingOnly && !answerOnly), [schedules, memberFilter, awaitingOnly, answerOnly]);
  // Lines the default view hides — the toggle must stay while they are shown.
  const hiddenCount = (items || []).filter((it) => HIDDEN_STATUSES.includes(it.status)).length;

  // Group by the month the line RELATES to, newest first.
  const groups = useMemo(() => {
    const out = []; let cur = null;
    visible.forEach((it) => {
      const k = String(it.relates_to_month || it.month || '').slice(0, 7);
      if (!cur || cur.key !== k) { cur = { key: k, rows: [], booked: 0, awaiting: 0, toConfirm: 0 }; out.push(cur); }
      cur.rows.push(it);
      if (it.status === 'confirmed') cur.booked += Number(it.amount || 0);
      if (isAwaiting(it)) cur.awaiting += 1;
      if (needsAnswer(it)) cur.toConfirm += 1;
    });
    return out;
  }, [visible]);

  const booked = useMemo(() => (items || []).filter((it) => it.status === 'confirmed').reduce((s, it) => s + Number(it.amount || 0), 0), [items]);

  const toggle = (id) => setOpen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const remove = async (it) => {
    const reason = window.prompt(`Remove this ${fmt$(it.amount)} line ("${it.note}")? It leaves the books; the reason stays on the line.`, '');
    if (reason == null) return;
    const sb = getSupabase();
    const { error: err } = await sb.rpc('v2_admin_remove_cost_item', { p_item_id: it.id, p_reason: reason || null });
    if (err) { setFlash('Remove failed: ' + (err.message || err)); return; }
    setFlash('Line removed.');
    load();
  };

  // Expected charge → answered (same RPCs as the Home card).
  const answer = async (fn, done) => {
    setBusy(true); setFlash(null);
    try {
      const sb = getSupabase();
      await fn(sb);
      setInline(null);
      setFlash(done);
      await load();
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
    } catch (e) { setFlash((e?.message || String(e))); }
    finally { setBusy(false); }
  };
  const confirmCharge = (it, amount) => answer(async (sb) => {
    const { error: err } = await sb.rpc('v2_confirm_cost_occurrence', { p_item_id: it.id, p_amount: amount ?? null });
    if (err) throw err;
  }, amount != null && Number(amount) !== Number(it.amount) && !actingAsAdmin
    ? 'Sent to Kareem — the edited figure needs his confirmation before it counts.'
    : 'Confirmed — the charge is in the books now.');
  const skipCharge = (it) => answer(async (sb) => {
    const { error: err } = await sb.rpc('v2_skip_cost_occurrence', { p_item_id: it.id, p_note: null });
    if (err) throw err;
  }, 'Recorded as did not happen — nothing counts.');

  const onSaved = ({ mode }) => {
    setEditing(null);
    setFlash(mode === 'pending_edit'
      ? 'Sent to Kareem for confirmation — the line keeps its current figure until he confirms.'
      : mode === 'edited_pending' ? 'Updated — still awaiting Kareem.' : mode === 'unchanged' ? 'Nothing changed.' : 'Saved.');
    load();
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
  };

  if (items == null) return <section className="card cost-lines-card" style={S.card}><div style={S.empty}>Loading cost lines…</div></section>;

  const showMemberPills = (members || []).length > 1;
  const showFilters = showMemberPills || awaitingCount > 0 || answerCount > 0;

  return (
    <section className="card cost-lines-card" style={S.card}>
      <div style={S.eye}>Costs · line by line</div>
      <h2 style={S.title}>{fmt$(booked)} in the books</h2>
      <p style={S.sub}>
        Every cost counted against this household, plus what is coming up on a schedule. Tap a line for details, history and actions.{' '}
        {actingAsAdmin
          ? 'You are acting as the investor: edits book immediately and Remove takes a line out of the books.'
          : 'You can edit the lines you entered — a change to a booked line goes to Kareem for confirmation.'}
      </p>
      {flash && <div style={{ fontSize: 12.5, color: '#047857', margin: '0 0 8px' }} className="cost-lines-flash">{flash}</div>}
      {error && <div style={S.err}>{error}</div>}
      {showFilters && (
        <div className="cl-filters">
          {showMemberPills && (
            <>
              <button type="button" className="cl-pill" aria-pressed={memberFilter == null} onClick={() => setMemberFilter(null)}>Everyone</button>
              {members.map((m) => (
                <button key={m.id} type="button" className="cl-pill" aria-pressed={memberFilter === m.id} onClick={() => setMemberFilter(memberFilter === m.id ? null : m.id)}>{m.name}</button>
              ))}
            </>
          )}
          {answerCount > 0 && (
            <button type="button" className={'cl-pill cl-pill-answer cost-lines-answer' + (awaitingCount > 0 ? '' : ' cl-pill-right')} aria-pressed={answerOnly} onClick={() => { setAnswerOnly((v) => !v); setAwaitingOnly(false); }}>
              Needs your answer ({answerCount})
            </button>
          )}
          {awaitingCount > 0 && (
            <button type="button" className="cl-pill cl-pill-right cost-lines-awaiting" aria-pressed={awaitingOnly} onClick={() => { setAwaitingOnly((v) => !v); setAnswerOnly(false); }}>
              Awaiting Kareem ({awaitingCount})
            </button>
          )}
        </div>
      )}
      {upcoming.length > 0 && (
        <div className="cl-upcoming">
          <div className="cl-month">
            <span>Coming up</span>
            <b>{upcoming.length} on a schedule</b>
          </div>
          {upcoming.map((sc) => {
            const isOpen = open.has('s:' + sc.id);
            const who = nameOf[sc.member_id] || sc.member_id;
            const label = SCHED_LABEL[sc.status];
            return (
              <div key={sc.id} className="cost-line cost-upcoming" data-status={sc.status} data-open={isOpen} data-faded={sc.status !== 'active'}>
                <div className="cost-line-head" role="button" tabIndex={0} aria-expanded={isOpen}
                  onClick={() => toggle('s:' + sc.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle('s:' + sc.id); } }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="cl-title">{sc.name}</div>
                    <div className="cl-meta">
                      <span>{who}</span>
                      <span className="cl-dim">·</span>
                      <span>{sc.status === 'active' && sc.next_due ? `due ${dayLabel(sc.next_due)}` : `every month on the ${ordinal(sc.bill_day)}`}</span>
                      {sc.paid_by === 'kareem' ? <><span className="cl-dim">·</span><span>Kareem pays</span></> : null}
                      {label ? <span className="cl-chip" style={chipStyle(sc.status === 'pending_approval' ? CHIP.pending : CHIP.skipped)}>{label}</span> : <span className="cl-chip" style={chipStyle(CHIP.skipped)}>Upcoming</span>}
                    </div>
                  </div>
                  <div className="cl-amt cl-amt-soft">{fmt$(sc.amount)}<span className="cl-chev" aria-hidden="true">›</span></div>
                </div>
                {isOpen && (
                  <div className="cl-panel cost-line-panel">
                    <dl>
                      {sc.note ? <><dt>Detail</dt><dd>{sc.note}</dd></> : null}
                      <dt>Repeats</dt><dd>every month on the {ordinal(sc.bill_day)}</dd>
                      <dt>Paid by</dt><dd>{sc.paid_by === 'kareem' ? 'Kareem' : who}</dd>
                      <dt>Status</dt><dd>{sc.status === 'active' ? 'Live' : label}</dd>
                    </dl>
                    <div className="cl-note">
                      {sc.status === 'active'
                        ? `Nothing counts yet. On ${sc.next_due ? dayLabel(sc.next_due) : 'the bill day'} this shows up below as an expected charge${sc.paid_by === 'kareem' ? ' for Kareem to confirm.' : ', and you confirm it once the charge has gone through.'}`
                        : sc.status === 'paused' ? 'Paused: no expected charges until it is switched back on.' : 'Kareem has not approved this schedule yet. It starts producing expected charges once he does.'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {visible.length === 0 ? (
        <div style={S.empty}>{(items || []).length === 0 ? 'No cost lines yet.' : 'Nothing matches this filter.'}</div>
      ) : groups.map((g) => (
        <div key={g.key}>
          <div className="cl-month">
            <span>{monthLabel(g.key + '-01')}</span>
            <b>{fmt$(g.booked)} booked · {g.rows.length} line{g.rows.length === 1 ? '' : 's'}{g.awaiting ? `, ${g.awaiting} awaiting` : ''}{g.toConfirm ? `, ${g.toConfirm} to confirm` : ''}</b>
          </div>
          {g.rows.map((it) => {
            const editable = actingAsAdmin ? ['confirmed', 'pending', 'suggested'].includes(it.status) : isInvestorEditable(it);
            const removable = actingAsAdmin && it.status !== 'removed';
            const pe = it.pending_edit;
            const struck = it.status === 'removed' || it.status === 'rejected';
            const faded = struck || it.status === 'skipped';
            const isOpen = open.has(it.id);
            const { title, detail } = splitNote(it.note);
            const history = parseHistory(it.review_note);
            const edits = history.filter((h) => h.verb === 'edited').length;
            const last = history.length ? history[history.length - 1] : null;
            const byKareem = it.source === 'admin';
            const who = nameOf[it.member_id] || it.member_id;
            // T-064: the line's own date; older lines fall back to a date inside the note.
            const noteDate = it.occurred_on ? String(it.occurred_on).slice(0, 10) : (String(it.note || '').match(/\b(\d{4}-\d{2}-\d{2})\b/) || [])[1];
            const bits = [who];
            if (noteDate) bits.push(dayLabel(noteDate));
            if (it.paid_by === 'kareem') bits.push('Kareem paid');
            if (it.relates_to_month) bits.push(`posted in ${monthLabel(it.month)}`);
            if (!struck && edits > 0) bits.push(`edited ${edits === 1 ? 'once' : edits === 2 ? 'twice' : `${edits} times`}`);
            return (
              <div key={it.id} className="cost-line" data-status={it.status} data-open={isOpen} data-struck={struck} data-faded={faded}
                data-editable={editable} data-removable={removable}>
                <div className="cost-line-head" role="button" tabIndex={0} aria-expanded={isOpen}
                  onClick={() => toggle(it.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(it.id); } }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="cl-title">{title || '(no note)'}</div>
                    <div className="cl-meta">
                      {bits.map((b, i) => <React.Fragment key={i}>{i > 0 ? <span className="cl-dim">·</span> : null}<span className={i > 0 && /^edited /.test(b) ? 'cl-dim' : undefined}>{b}</span></React.Fragment>)}
                      {it.status === 'suggested' && !needsAnswer(it) ? <span className="cl-chip" style={chipStyle(CHIP.pending)}>Expected — Kareem confirms</span>
                        : it.status !== 'confirmed' ? <StatusChip it={it} /> : null}
                      {pe ? <span className="cl-chip cost-line-edit-chip" style={chipStyle(['#eef2ff', '#3730a3', '#c7d2fe'])}>Change to {fmt$(pe.amount)} awaiting Kareem</span> : null}
                      {struck && last ? <span className="cl-dim">{last.who ? `${VERB[last.verb] || last.verb} by ${last.who}` : ''}{last.reason ? ` — ${last.reason}` : ''}</span> : null}
                    </div>
                  </div>
                  <div className="cl-amt">{fmt$(it.amount)}<span className="cl-chev" aria-hidden="true">›</span></div>
                </div>
                {isOpen && (
                  <div className="cl-panel cost-line-panel">
                    <dl>
                      {detail ? <><dt>Detail</dt><dd>{detail}</dd></> : null}
                      {it.occurred_on ? <><dt>Date</dt><dd>{dayLabel(it.occurred_on)}</dd></> : null}
                      <dt>Paid by</dt><dd>{it.paid_by === 'kareem' ? 'Kareem' : who}</dd>
                      <dt>Entered by</dt><dd>{byKareem ? 'Kareem' : (nameOf[it.created_by] || who)}</dd>
                      {it.relates_to_month ? <><dt>Posted in</dt><dd>{monthLabel(it.month)} (relates to {monthLabel(it.relates_to_month)})</dd></> : null}
                      <dt>Status</dt><dd>{STATUS_LABEL[it.status] || it.status}{pe ? ` · change to ${fmt$(pe.amount)} awaiting Kareem` : ''}</dd>
                      {it.created_at ? <><dt>Logged</dt><dd>{dayLabel(it.created_at)}</dd></> : null}
                    </dl>
                    {history.length > 0 && (
                      <div className="cl-history">
                        <div className="cl-history-h">History</div>
                        <ul>
                          {history.slice().reverse().map((h, i) => (
                            <li key={i}>
                              {h.date ? <b>{dayLabel(h.date)}</b> : null}{' '}
                              {h.who ? `${h.who} ${VERB[h.verb] || h.verb}` : ''}
                              {h.rest ? <span className="cl-was"> ({trunc(h.rest, 140)})</span> : null}
                              {h.reason ? <> — {h.reason}</> : null}
                              {!h.who ? h.raw : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {needsAnswer(it) && (
                      inline && inline.id === it.id ? (
                        <div className="cl-actions cl-answer">
                          <input type="number" min="0.01" step="0.01" inputMode="decimal" autoFocus className="cl-input cost-line-answer-amount" value={inline.value} disabled={busy}
                            onChange={(e) => setInline({ id: it.id, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Escape') setInline(null); }} />
                          <button type="button" className="cl-confirm cost-line-answer-send" disabled={busy || !(Number(inline.value) > 0)} onClick={() => confirmCharge(it, Number(inline.value))}>
                            {actingAsAdmin ? 'Book this amount' : 'Send to Kareem'}
                          </button>
                          <button type="button" className="cl-btn" disabled={busy} onClick={() => setInline(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="cl-actions cl-answer">
                          <button type="button" className="cl-confirm cost-line-confirm" disabled={busy} onClick={() => confirmCharge(it, null)}>Confirm charge</button>
                          <button type="button" className="cl-btn cost-line-skip" disabled={busy} onClick={() => skipCharge(it)}>Didn't happen</button>
                          <button type="button" className="cl-btn cost-line-answer-edit" disabled={busy} onClick={() => setInline({ id: it.id, value: String(it.amount) })}>Different amount</button>
                        </div>
                      )
                    )}
                    {(editable || removable) && (
                      <div className="cl-actions">
                        {editable && !pe && <button type="button" className="cl-btn cost-line-edit" onClick={() => setEditing(it)}>Edit</button>}
                        {editable && pe && !actingAsAdmin && <span className="cl-wait">waiting on Kareem</span>}
                        {removable && <button type="button" className="cl-link cost-line-remove" onClick={() => remove(it)}>Remove from the books…</button>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {hiddenCount > 0 && (
        <button type="button" style={{ ...S.btn, marginTop: 8 }} className="cost-lines-toggle" onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Hide ${hiddenCount} removed, rejected or skipped line${hiddenCount === 1 ? '' : 's'}` : `Show ${hiddenCount} removed, rejected or skipped line${hiddenCount === 1 ? '' : 's'}`}
        </button>
      )}
      {editing && (
        <CostEditModal item={editing} mode={actingAsAdmin ? 'admin' : 'investor'} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </section>
  );
}
